import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useAuth, API_URL } from '../context/AuthContext';

const LABEL_TIPO = { militar: 'Inglés Militar (SLP)', civil: 'Inglés Civil (Cambridge)' };
const ICONO_TIPO  = { militar: '🎖️', civil: '🎓' };

export default function PagoAcceso() {
  const { usuario, logout, actualizarUsuario } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig]   = useState(null);
  const [error, setError]     = useState('');
  const [cargando, setCargando] = useState(true);
  const [activando, setActivando] = useState(false);

  if (!usuario) { navigate('/'); return null; }
  if (usuario.suscripcion_activa) { navigate('/seleccion'); return null; }

  const accesos = usuario.accesos || [];
  const esCaducada = !usuario.suscripcion_activa && !!usuario.tuvo_suscripcion;

  useEffect(() => {
    axios.get(`${API_URL}/pagos/config`)
      .then(r => setConfig(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error cargando configuración de pago'))
      .finally(() => setCargando(false));
  }, []);

  const onApprove = async (data) => {
    setActivando(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/pagos/confirmar`, {
        subscription_id: data.subscriptionID,
      });
      if (res.data.pagado) {
        actualizarUsuario({ ...usuario, ...res.data.usuario, accesos: usuario.accesos });
        navigate('/pago-exitoso');
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Error activando la suscripción. Contacta con el administrador.');
      setActivando(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #FFF0F5 0%, #FFE4EF 60%, #FDF2F8 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div className="fade-in" style={{ width: '100%', maxWidth: '480px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '48px', marginBottom: '10px' }}>🌸</div>
          <h1 style={{ fontSize: '26px', color: '#581C47', fontFamily: 'Inter, sans-serif', marginBottom: '4px' }}>Su Academia</h1>
          <p style={{ color: '#BE84A3', fontSize: '14px' }}>Campus Virtual</p>
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #FBCFE8', borderRadius: '20px', padding: '32px', marginBottom: '16px', boxShadow: '0 8px 32px rgba(236,72,153,0.10)' }}>

          {/* Banner suscripción caducada */}
          {esCaducada && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '12px', padding: '14px 16px', marginBottom: '22px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <div>
                <p style={{ color: '#DC2626', fontWeight: 700, fontSize: '14px', margin: '0 0 2px' }}>Suscripción inactiva</p>
                <p style={{ color: '#EF4444', fontSize: '13px', margin: 0 }}>
                  Tu suscripción ha caducado o fue cancelada. Renuévala para volver a acceder.
                </p>
              </div>
            </div>
          )}

          <h2 style={{ color: '#581C47', fontSize: '20px', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>
            {esCaducada ? 'Renovar suscripción' : 'Activar suscripción'}
          </h2>
          <p style={{ color: '#BE84A3', fontSize: '14px', marginBottom: '24px' }}>
            Hola {usuario.nombre?.split(' ')[0]}. {esCaducada
              ? 'Reactiva tu acceso para continuar estudiando.'
              : 'Activa tu suscripción mensual para acceder a todos los contenidos.'}
          </p>

          {/* Plan del usuario */}
          {accesos.length > 0 && (
            <div style={{ background: '#FFF0F5', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px' }}>
              <p style={{ color: '#BE84A3', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 10px' }}>
                Tu programa de estudios
              </p>
              {accesos.map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: i < accesos.length - 1 ? '8px' : 0 }}>
                  <span style={{ fontSize: '20px' }}>{ICONO_TIPO[a.tipo] || '📚'}</span>
                  <div>
                    <p style={{ color: '#581C47', fontWeight: 700, fontSize: '14px', margin: 0 }}>{LABEL_TIPO[a.tipo] || a.tipo}</p>
                    <p style={{ color: '#BE84A3', fontSize: '12px', margin: '1px 0 0' }}>Nivel {a.nivel}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Precio */}
          <div style={{ background: '#FFF0F5', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '18px', marginBottom: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ color: '#EC4899', fontSize: '14px', fontWeight: 600 }}>Acceso completo</span>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#581C47', fontWeight: 800, fontSize: '26px', fontFamily: 'Inter, sans-serif' }}>
                  {config ? `${config.precio}€` : cargando ? '...' : '60€'}
                </span>
                <span style={{ color: '#BE84A3', fontSize: '13px', marginLeft: '4px' }}>/mes</span>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #FBCFE8', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                'Acceso ilimitado a todo el temario',
                'Actividades y exámenes de práctica',
                'Clases online y grabadas',
                'Calendario de eventos y clases',
              ].map(item => (
                <p key={item} style={{ color: '#BE84A3', fontSize: '13px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#EC4899', fontWeight: 700 }}>✓</span> {item}
                </p>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', padding: '12px', color: '#EF4444', fontSize: '13px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {activando && (
            <div style={{ textAlign: 'center', color: '#EC4899', fontSize: '14px', marginBottom: '16px', padding: '12px', background: '#FFF0F5', borderRadius: '10px' }}>
              ⏳ Activando tu suscripción...
            </div>
          )}

          {cargando && (
            <div style={{ textAlign: 'center', color: '#BE84A3', fontSize: '14px', padding: '20px 0' }}>
              Cargando método de pago...
            </div>
          )}

          {config && !activando && (
            <PayPalScriptProvider options={{
              'client-id': config.clientId,
              vault: true,
              intent: 'subscription',
              currency: 'EUR',
              components: 'buttons',
            }}>
              <PayPalButtons
                createSubscription={(data, actions) =>
                  actions.subscription.create({
                    plan_id: config.planId,
                    application_context: {
                      brand_name: 'Su Academia',
                      locale: 'es-ES',
                      shipping_preference: 'NO_SHIPPING',
                      user_action: 'SUBSCRIBE_NOW',
                    },
                  })
                }
                onApprove={onApprove}
                onError={(err) => {
                  console.error('PayPal error:', JSON.stringify(err));
                  const msg = err?.message || err?.toString() || 'Error desconocido';
                  setError(`Error PayPal: ${msg}`);
                }}
                onCancel={() => setError('')}
                style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe' }}
              />
            </PayPalScriptProvider>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button onClick={() => { logout(); navigate('/'); }}
            style={{ background: 'none', border: 'none', color: '#BE84A3', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}>
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
